import { userApi } from "../api";
import { EMAIL_REGEX } from "../config";
import { validationError } from "./serviceError";

// Profile edit rules. Validation lives here rather than in the React hook, so
// the rules are one import away from anywhere and testable on their own.

/**
 * Check a field value and return it trimmed. Synchronous, so callers can reject
 * bad input before they show any in-progress state.
 *
 * @param {"name"|"email"} field
 * @param {string} value raw input from the field
 * @returns {string} the trimmed value
 * @throws {Error} tagged with `isValidation` and a severity
 */
export const validateProfileField = (field, value) => {
  const trimmed = (value || "").trim();

  if (field === "name") {
    if (!trimmed) throw validationError("Name cannot be empty.", "warn");
    return trimmed;
  }

  if (!trimmed) throw validationError("Email cannot be empty.", "warn");
  if (!EMAIL_REGEX.test(trimmed)) throw validationError("Invalid email.");
  return trimmed;
};

/**
 * Rename the account.
 *
 * @param {string} value raw input from the field
 * @returns {Promise<{username: string}>} the trimmed value that was saved
 */
export const changeUsername = async (value) => {
  const username = validateProfileField("name", value);

  await userApi.updateUsername(username);
  return { username };
};

/**
 * Change the account email.
 *
 * @param {string} value raw input from the field
 * @returns {Promise<{email: string}>} the trimmed value that was saved
 */
export const changeEmail = async (value) => {
  const email = validateProfileField("email", value);

  await userApi.updateEmail(email);
  return { email };
};

/**
 * Apply an edit to whichever field is open.
 *
 * @param {"name"|"email"} field
 * @param {string} value
 * @returns {Promise<{patch: Object, message: string}>} the patch to merge into
 *   the user object, and the success message to show
 */
export const saveProfileField = async (field, value) => {
  if (field === "name") {
    const { username } = await changeUsername(value);
    return { patch: { username }, message: "Username updated!" };
  }

  const { email } = await changeEmail(value);
  return { patch: { email }, message: "Email updated!" };
};
